import React from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { AspopResult, aspopUrl, ripeUrl } from '../../utils/apnic';
import { formatCompact, formatInt } from '../../utils/format';
import { DataBadge } from '../DataBadge';
import { SourceRef } from '../SourceRef';
interface TopNetworksProps {
  cca2: string;
  countryName: string;
  population: number;
  result: AspopResult | null;
}
export function TopNetworks({
  cca2,
  countryName,
  population,
  result
}: TopNetworksProps) {
  const rows = result?.rows ?? [];
  const max = Math.max(1, ...rows.map(r => r.users));
  const src = aspopUrl(cca2);
  return <section aria-labelledby="networks-title" className="mt-12" data-id="element-170" data-mp="28kd0nqlp4pb3">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-171" data-mp="2dk7rxv9dvgwo">
        <h2 id="networks-title" className="font-serif text-2xl text-ink" data-id="element-172" data-mp="2diek4cn1aqn3">
          Top networks
          <SourceRef id="apnicAspop" href={src} data-id="element-173" data-mp="3k2nu0ea3txsj" />
        </h2>
        <DataBadge mode={result ? result.mode : 'loading'} source="APNIC aspop" asOf={result?.asOf} data-id="element-174" data-mp="3oyl8sg5pyw37" />
      </div>
      <p className="mt-1 text-[13px] text-muted" data-id="element-175" data-mp="2hbmsu997sbvd">
        The autonomous systems that carry {countryName}’s internet users, by APNIC’s ad-based user estimate.
        {result?.mode === 'snapshot' && ' Live table unavailable — showing the embedded top-5 snapshot.'}
      </p>

      {!result && <p className="mt-6 py-8 text-center font-mono text-xs text-muted" role="status" data-id="element-176" data-mp="23bvjiltn7jnp">
          Fetching APNIC table…
        </p>}

      {result && rows.length === 0 && <p className="mt-6 rounded-lg border border-line bg-surface px-4 py-6 text-center text-sm text-muted" data-id="element-177" data-mp="1tlj04wxkdwj5">
          No live table could be read and no embedded snapshot exists for this country — no figures are shown rather than guessed.
        </p>}

      {rows.length > 0 && <>
          <div className="mt-5 hidden grid-cols-[2rem_minmax(0,1fr)_6.5rem_6rem_6rem] gap-x-4 border-b border-line pb-2 font-mono text-[10.5px] uppercase tracking-wider text-muted md:grid" data-id="element-178" data-mp="1flhoqnjob6pf">
            <span data-id="element-179" data-mp="10z2hyh1upesf">#</span>
            <span data-id="element-180" data-mp="10ysqsq52l1l5">Operator · ASN</span>
            <span className="text-right" data-id="element-181" data-mp="15yx98lpjg6e0">Est. users</span>
            <span className="text-right" data-id="element-182" data-mp="160rs7uh91ofg">% of pop.</span>
            <span className="text-right" data-id="element-183" data-mp="rzxin8igthf7">% global</span>
          </div>
          <ol className="divide-y divide-line/60" data-id="element-184" data-mp="s1s3kmtql9ja">
            {rows.map(r => <li key={r.asn} className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 py-3 md:grid-cols-[2rem_minmax(0,1fr)_6.5rem_6rem_6rem]" data-id="element-185" data-mp="w33owg5kv8lo">
                <span className="font-mono text-[12px] text-muted" data-id="element-186" data-mp="1oode1l89al08">{r.rank}</span>
                <div className="min-w-0" data-id="element-187" data-mp="1ax8jgxjqwiwr">
                  <p className="flex items-baseline gap-2 truncate text-[13.5px] text-ink" data-id="element-188" data-mp="1aw762mf9bkbv">
                    <span className="truncate" data-id="element-189" data-mp="1ey249i1w5hlf">{r.name}</span>
                    <span className="shrink-0 font-mono text-[11px] text-human" data-id="element-190" data-mp="22oen3nyocyqs">{r.asn}</span>
                  </p>
                  <div className="mt-1.5 h-1.5 rounded-full bg-raised" aria-hidden="true" data-id="element-191" data-mp="27gzbub2q4lng">
                    <div className="h-full rounded-full bg-human/75" style={{
                width: `${r.users / max * 100}%`
              }} data-id="element-192" data-mp="3s5a81kf2lgj1" />
                  </div>
                </div>
                <span className="text-right font-mono text-[13px] tabular text-ink" title={`${formatInt(r.users)} users`} data-id="element-193" data-mp="5qqs3jag5tag">
                  {formatCompact(r.users)}
                  <SourceRef id="apnicAspop" href={src} estimate={{
              low: formatCompact(r.users * 0.85),
              high: formatCompact(r.users * 1.15)
            }} note="APNIC user counts are estimates from ad-measurement sampling." data-id="element-194" data-mp="u0dz1i7d7ro" />
                </span>
                <span className="col-start-2 font-mono text-[12px] tabular text-muted md:col-start-auto md:text-right" data-id="element-195" data-mp="3j5n1ylbebmjn">
                  <span className="md:hidden" data-id="element-196" data-mp="3e5se6qmx3d2t">pop. </span>
                  {population > 0 ? `${(r.users / population * 100).toFixed(1)}%` : '—'}
                </span>
                <span className="font-mono text-[12px] tabular text-muted md:text-right" data-id="element-197" data-mp="tguvpqnmxt1g">
                  <span className="md:hidden" data-id="element-198" data-mp="2z4vawd6s11tb">global </span>
                  {r.pctInternet != null ? `${r.pctInternet.toFixed(2)}%` : '—'}
                </span>
              </li>)}
          </ol>
        </>}

      <a href={ripeUrl(cca2)} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-[13px] text-muted hover:text-machine" data-id="element-199" data-mp="385a7i8p6y3c4">
        Full ASN registry →
        <span className="sr-only" data-id="element-200" data-mp="3i498d53psuok">(RIPEstat, opens in new tab)</span>
        <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-201" data-mp="3i1wq0qu1oiaz" />
      </a>
      <SourceRef id="ripestat" href={ripeUrl(cca2)} data-id="element-202" data-mp="3n0znnq7p3m8x" />
    </section>;
}